import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Send, ArrowUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function FloatingWidgets() {
  const location = useLocation();
  
  // AI Assistant states
  const [aiOpen, setAiOpen] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { sender: "ai", text: "Hello! I am the Supply Spot AI Assistant. How can I help you today?" }
  ]);
  const [aiInput, setAiInput] = useState("");

  // Scroll to Top state
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", checkScroll);
    return () => window.removeEventListener("scroll", checkScroll);
  }, []);

  // Close chatbot when route changes
  useEffect(() => {
    setAiOpen(false);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleAiSend = () => {
    if (!aiInput.trim()) return;
    const userMsg = aiInput;
    setAiMessages(prev => [...prev, { sender: "user", text: userMsg }]);
    setAiInput("");

    // Simulate quick custom bot replies
    setTimeout(() => {
      let botResponse = "I have logged your request. One of our procurement consultants will reach out to you shortly.";
      if (userMsg.toLowerCase().includes("pricing")) {
        botResponse = "Our plans start at ₹7,999/month billed annually. I can help you schedule a sales discussion to get a custom quote!";
      } else if (userMsg.toLowerCase().includes("demo") || userMsg.toLowerCase().includes("book")) {
        botResponse = "You can schedule an interactive walkthrough by clicking 'Book Demo' on our Contact page!";
      } else if (userMsg.toLowerCase().includes("sap") || userMsg.toLowerCase().includes("integrate")) {
        botResponse = "Yes! We support bi-directional real-time integration with SAP (S/4HANA), Oracle Cloud, and major ledger systems.";
      }
      setAiMessages(prev => [...prev, { sender: "ai", text: botResponse }]);
    }, 1000);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 select-none">
      {/* Chat Drawer */}
      <AnimatePresence>
        {aiOpen && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="w-80 h-96 bg-bg-secondary/95 backdrop-blur-md border border-brand-orange/35 rounded-2xl shadow-2xl flex flex-col justify-between mb-2 overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 bg-brand-orange/10 border-b border-brand-orange/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-brand-orange animate-ping" />
                <span className="text-[10px] font-title font-bold text-text-primary tracking-widest uppercase">
                  AETHER COPILOT CORE
                </span>
              </div>
              <button 
                onClick={() => setAiOpen(false)} 
                className="text-text-secondary hover:text-text-primary text-xs cursor-pointer"
              >
                Close
              </button>
            </div>

            {/* Message History */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 scrollbar-thin scrollbar-thumb-orange-500/30">
              {aiMessages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`text-[11px] p-2.5 rounded-xl leading-relaxed max-w-[85%] ${
                    msg.sender === "user"
                      ? "bg-brand-orange/10 border border-brand-orange/30 text-text-primary self-end"
                      : "bg-bg-primary border border-border-theme text-text-secondary self-start"
                  }`}
                >
                  {msg.text}
                </div>
              ))}
            </div>

            {/* Message inputs */}
            <div className="p-3 border-t border-border-theme flex gap-2 bg-bg-secondary">
              <input
                type="text"
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAiSend()}
                placeholder="Ask product diagnostics..."
                className="flex-1 px-3 py-2 bg-bg-primary border border-border-theme focus:border-brand-orange rounded-lg text-xs text-text-primary focus:outline-none"
              />
              <button
                onClick={handleAiSend}
                className="px-3 bg-brand-orange hover:bg-brand-amber rounded-lg flex items-center justify-center text-white transition-colors duration-300 cursor-pointer"
              >
                <Send className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Action Buttons Container */}
      <div className="flex flex-col gap-3 items-center">
        {/* Scroll to Top Button */}
        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 10 }}
              onClick={scrollToTop}
              className="w-12 h-12 rounded-full bg-bg-secondary/80 border border-brand-orange/30 hover:border-brand-orange/80 hover:bg-brand-orange/10 text-brand-orange flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-all duration-300 backdrop-blur-md cursor-pointer relative group"
              aria-label="Scroll to Top"
            >
              <ArrowUp className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
              
              {/* Tooltip */}
              <span className="absolute right-14 scale-0 group-hover:scale-100 transition-all duration-250 bg-bg-secondary border border-border-theme px-2.5 py-1 rounded text-[9px] font-bold text-text-primary whitespace-nowrap shadow-md uppercase tracking-wider">
                Scroll To Top
              </span>
            </motion.button>
          )}
        </AnimatePresence>

        {/* Chatbot Button */}
        <button
          onClick={() => setAiOpen(!aiOpen)}
          className="w-14 h-14 rounded-full bg-brand-orange hover:bg-brand-amber text-white flex items-center justify-center shadow-xl shadow-brand-orange/20 hover:scale-[1.05] active:scale-[0.95] transition-all duration-300 relative cursor-pointer group"
          aria-label="Open AI Assistant"
        >
          {/* Hologram Pulse Ring */}
          <span className="absolute inset-0 rounded-full border border-brand-orange animate-ping opacity-75 pointer-events-none" />
          <Bot className="w-6 h-6" />
          
          {/* Tooltip */}
          <span className="absolute right-16 scale-0 group-hover:scale-100 transition-all duration-250 bg-bg-secondary border border-border-theme px-2.5 py-1 rounded text-[9px] font-bold text-text-primary whitespace-nowrap shadow-md uppercase tracking-wider">
            Aether Copilot
          </span>
        </button>
      </div>
    </div>
  );
}
